import React from "react";
import { ListTree } from "lucide-react";
import { usePortalBranding } from "../../hooks/usePortalBranding";
import { MIN_TABLE_OF_CONTENTS_HEADINGS, type ArticleHeading } from "../utils/useArticleHeadings";

interface ArticleTableOfContentsProps {
  headings: ArticleHeading[];
  className?: string;
}

/** "On this page": links to a long article's sections (shown from three headings) */
export const ArticleTableOfContents: React.FC<ArticleTableOfContentsProps> = ({ headings, className }) => {
  const { getThemedPrimaryColor } = usePortalBranding();
  if (headings.length < MIN_TABLE_OF_CONTENTS_HEADINGS) return null;

  return (
    <nav aria-label="On this page" className={className}>
      <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-2">
        <ListTree className="h-4 w-4" aria-hidden="true" />
        On this page
      </p>
      <ul className="space-y-1 text-sm">
        {headings.map((heading) => (
          <li key={heading.id} style={{ paddingLeft: `${(Math.min(heading.depth, 3) - 1) * 12}px` }}>
            <a
              href={`#${heading.id}`}
              onClick={(e) => {
                // Scroll without the router treating the hash as a navigation
                e.preventDefault();
                const target = document.getElementById(heading.id);
                const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
                target?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
                // Keyboard and screen reader users continue from the section; it isn't a control, so no focus ring
                target?.setAttribute("tabindex", "-1");
                if (target) target.style.outline = "none";
                target?.focus({ preventScroll: true });
              }}
              className="block py-0.5 text-gray-600 dark:text-gray-400 hover:underline"
              onMouseEnter={(e) => (e.currentTarget.style.color = getThemedPrimaryColor())}
              onMouseLeave={(e) => (e.currentTarget.style.color = "")}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};
